import { Dispatch, SetStateAction, useState, useEffect, useRef, MutableRefObject } from 'react';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './PDFViewer.css';
import './PDFListView.css';
import {
  Modal,
  ModalConfig,
  ModalPositionX,
  ModalPositionY,
  ModalContent,
  ModalFooter,
  SecondaryButton,
  PrimaryButton,
} from '../../../shared/modal/Modal';
import DocumentViewer from './PDFViewer';
import { Property } from '../../../../models/Property';

const PropertyDocumentConfig: ModalConfig = {
  title: "Property Documents",
  showHeader: true,
  positionX: ModalPositionX.center,
  positionY: ModalPositionY.center,
  showOverlay: true,
};

interface DocumentListViewProps {
  src: string; // The source array should be a string array
  showDocumentModal: boolean;
  setShowDocumentModal: Dispatch<SetStateAction<boolean>>;
  setSrc: Dispatch<SetStateAction<string>>; // Function to update src state
  property: Property;
}

export default function DocumentListView({
  src,
  showDocumentModal,
  setShowDocumentModal,
  setSrc,
  property
}: DocumentListViewProps) {
  const [documentSrc, setDocumentSrc] = useState<string>(''); // State to store the selected document URL
  const [viewingDocument, setViewingDocument] = useState(false); // State to track whether the document is being viewed

  const modalContentRef = useRef<HTMLDivElement | null>(null);

  // Sync src state with the property documents whenever the property changes
  useEffect(() => {
    setSrc(property.brochureUrl || ""); // Ensure src is a valid string
  }, [property, setSrc]); // This effect will run whenever the property changes

  // Open Document Viewer with the selected URL
  const openDocumentViewer = (url: string) => {
    if (!url.toLowerCase().endsWith('.pdf')) {
      alert('The selected document is not a PDF file.');
      return;
    }
  
    setDocumentSrc(url);
    setViewingDocument(true);
  };
  
  // Back to the list view
  const backToList = () => {
    setViewingDocument(false); // Switch back to list view
    setDocumentSrc(''); // Clear the current document URL
  };

  const resetDocumentSrc = () => {
    setDocumentSrc(''); // Reset document source
    setViewingDocument(false); // Reset the viewing state
  };

  return (
    <Modal
      show={showDocumentModal}
      setShow={setShowDocumentModal}
      resetPdfSrc={resetDocumentSrc}
      config={PropertyDocumentConfig}
    >
      <ModalContent ref={modalContentRef} height={'calc(100vh - 385px)'} >
        {/* Toggle between list view and Document view */}
        {!viewingDocument ? (
          <>
            <div className="document-list-view">
              <div className="document-viewer-content">
                {(src || "").split(',').map((url, index) => (
                  <div key={index} className="document-item">
                    <span style={{ paddingRight: '15px' }}>{index + 1}. </span>
                      <span
                        className="document-url"
                        onClick={() => openDocumentViewer(url)}
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {url}
                      </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          // Display Document in the same modal when viewingDocument is true
          <DocumentViewer
            src={documentSrc}
            backToList={backToList} // Pass the backToList function
          />
        )}
      </ModalContent>
    </Modal>
  );
}
